import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';

export default function Tools() {
  const { user } = useAuth();
  const { currentRun, currentSession } = useAcademy();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('settings_gwaacademy');
        const data = Array.isArray(res) ? res : res?.data || [];
        const map = {};
        data.forEach(item => {
          if (item?.setting_key) map[item.setting_key] = item.value;
        });
        setSettings(map);
      } catch (err) {
        console.error('Failed to load tools settings', err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [currentRun?.id]);

  if (loading) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-muted">
        <SafeIcon name="Loader" className="mb-4 animate-spin text-4xl text-amber" />
        <p>Loading tools and event resources...</p>
      </div>
    );
  }

  let toolsList = [];
  try {
    if (settings.tools_buttons_json) {
      toolsList = JSON.parse(settings.tools_buttons_json);
    }
  } catch (e) {
    toolsList = [];
  }

  const headline = settings.tools_headline || 'Event Tools & Resources';
  const description = settings.tools_description || 
    'Access and download the exclusive templates, tools, and digital resources designed for this session.';
  const sessionTitle = currentRun?.title || currentRun?.name || currentSession?.title || 'Current Event Run';

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-fadeIn pb-20">
      {/* Header Section */}
      <header className="border-b-2 border-tan pb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <div className="inline-flex items-center gap-2 bg-forest-green/10 text-forest-green px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
            <SafeIcon name="Tool" /> {sessionTitle}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-dark-green font-poppins">
            {headline}
          </h1>
          <p className="text-muted text-base md:text-lg mt-2 max-w-3xl leading-relaxed whitespace-pre-wrap">
            {description}
          </p>
        </div>
      </header>

      {/* Tools Listing */}
      {toolsList.length === 0 ? (
        <Card className="border-2 border-dashed border-tan py-16 text-center bg-white">
          <div className="mx-auto w-16 h-16 bg-amber/10 rounded-full flex items-center justify-center text-3xl text-amber mb-4">
            <SafeIcon name="Tool" />
          </div>
          <h3 className="text-xl font-bold text-dark-green">Tools Coming Soon</h3>
          <p className="text-muted max-w-md mx-auto mt-2 text-sm">
            Event tools and downloadable resources will be published here by your academy instructors.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {toolsList.map((tool, idx) => (
            <Card 
              key={tool.id || idx} 
              className="flex flex-col justify-between border-t-4 border-t-forest-green shadow-sm hover:shadow-md transition-shadow bg-white p-6"
            >
              <div className="space-y-3 mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#EAF1E6] flex items-center justify-center text-forest-green text-2xl shrink-0">
                    <SafeIcon name={tool.icon || 'Download'} />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber bg-amber/10 px-2.5 py-1 rounded">
                    Tool #{idx + 1}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-dark-green font-poppins">
                  {tool.label || `Tool Resource ${idx + 1}`}
                </h3>
                {tool.description && (
                  <p className="text-muted text-sm leading-relaxed whitespace-pre-wrap">
                    {tool.description}
                  </p>
                )}
              </div>

              <a
                href={tool.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-auto"
              >
                <Button className="w-full bg-forest-green hover:bg-dark-green text-white py-3 font-semibold shadow-sm flex items-center justify-center gap-2">
                  <SafeIcon name="ExternalLink" className="text-base" />
                  {tool.label ? `Access ${tool.label}` : 'Open Tool'}
                </Button>
              </a>
            </Card>
          ))}
        </div>
      )}

      {/* Help / Guidance Card */}
      <Card className="bg-[#EAF1E6] border border-forest-green/20 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-forest-green text-white rounded-full shrink-0">
            <SafeIcon name="HelpCircle" className="text-xl" />
          </div>
          <div>
            <h4 className="font-bold text-dark-green text-base">Need help accessing your event tools?</h4>
            <p className="text-sm text-forest-green mt-1 leading-relaxed">
              All tools are provided for direct student access during your session. If a link requires permission or if you have questions, reach out in The Commons or contact support.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}